import React, { useEffect, useRef, useState, useCallback } from 'react';
import useBreakpoint from '@common/utils/useBreakpoint';
import { useInView } from 'react-intersection-observer';
import { scrollTo, easing } from '@utils/scrollUtils';
const isSafari = window.navigator.userAgent.match(/Safari/i);
const ScrollHandler = (props) => {
    const [active, setActive] = useState(false);
    const [offsetY, setOffsetY] = useState(0);
    const trackRef = useRef();
    const thumbRef = useRef();
    const initialY = useRef(0);
    const currentY = useRef(0);
    const trackLength = useRef(0);
    const galleryBottom = useRef();
    const scrollHandlerRef = useRef();
    const wrapperRef = useRef();
    const { ref: inViewRef, inView } = useInView({ initialInView: true });
    const currentBreakpoint = useBreakpoint();
    const [fixed, setFixed] = useState(true);
    const iOsLastClientY = useRef(0);
    const iOsChangeDirectionCounter = useRef(0);
    const iOsFirstClientY = useRef(0);
    const iOsScrollDirection = useRef('');
    const setRefs = useCallback((node) => {
        wrapperRef.current = node;
        // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
        inViewRef(node);
    }, [inViewRef]);
    useEffect(() => {
        const thumbBounds = thumbRef.current.getBoundingClientRect();
        initialY.current = thumbBounds.y;
        trackLength.current = trackRef.current.clientHeight - thumbRef.current.clientHeight;
        galleryBottom.current = document.querySelector('.product-col--gallery');
        return () => window.removeEventListener('scroll', scrollThumbnail);
    }, [currentBreakpoint]);
    useEffect(() => {
        var _a;
        let scrollHandlerNeeded = !inView && ((_a = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().y) >= 0; // User is scrolling the gallery
        if (scrollHandlerNeeded) {
            window.addEventListener('scroll', scrollThumbnail);
        }
        else {
            window.removeEventListener('scroll', scrollThumbnail);
        }
        setFixed(scrollHandlerNeeded);
    }, [inView, props.current]);
    const dragStart = (clientY) => {
        initialY.current = clientY - offsetY;
        setActive(true);
        document.documentElement.addEventListener('mousemove', dragMove);
        document.documentElement.addEventListener('touchmove', dragMove, { passive: false });
        document.documentElement.addEventListener('mouseup', dragEnd, { once: true });
        document.documentElement.addEventListener('touchend', dragEnd, { once: true });
    };
    const dragMove = (event) => {
        const isTouch = event instanceof TouchEvent;
        const clientY = isTouch ? event.touches[0].clientY : event.clientY;
        if (isSafari && isTouch && iOsFix(clientY)) {
            return;
        }
        const targetY = clientY - initialY.current;
        moveThumbnailTo(targetY);
    };
    const dragEnd = () => {
        document.documentElement.removeEventListener('mousemove', dragMove);
        document.documentElement.removeEventListener('touchmove', dragMove);
        setActive(false);
    };
    const moveThumbnailTo = (targetY) => {
        const clampedClientY = Math.min(Math.max(targetY, 0), trackLength.current);
        currentY.current = clampedClientY;
        setOffsetY(currentY.current);
        document.documentElement.scrollTop = calculateScrollValue();
    };
    const jumpTo = (event) => {
        const clientY = event instanceof TouchEvent ? event.touches[0].clientY : event.clientY;
        const targetY = clientY - trackRef.current.getBoundingClientRect().y;
        moveThumbnailTo(targetY);
    };
    const scrollThumbnail = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const clampedClientY = Math.min(calculateThumbPosition(), trackLength.current);
        currentY.current = clampedClientY;
        setOffsetY(currentY.current);
    };
    const calculateScrollValue = () => {
        return ((galleryBottom.current.clientHeight - window.innerHeight) / trackLength.current) * currentY.current;
    };
    const calculateThumbPosition = () => {
        return (trackLength.current / (galleryBottom.current.clientHeight - window.innerHeight)) * document.documentElement.scrollTop;
    };
    const scrollToBottom = () => {
        const targetScroll = galleryBottom.current.clientHeight;
        scrollTo(targetScroll, 1500, easing.easeInOutQuart);
    };
    /**
     * Ugly iOs Fix: apparentley scrolling the page during moveThumbnailTo() makes Safari lose track of the touch.clientY's position
     * resulting in little "jumps back" during the drag of the thumb.
     * The following prevents this by understanding the direction of the drag and thus avoids jumping back, unless enough force is applied
     */
    const iOsFix = (clientY) => {
        const minimumForce = 3;
        if (!iOsScrollDirection.current) {
            iOsScrollDirection.current = clientY > iOsFirstClientY.current ? 'down' : 'up';
        }
        if ((iOsScrollDirection.current === 'up' && clientY > iOsLastClientY.current) || (iOsScrollDirection.current === 'down' && clientY < iOsLastClientY.current)) {
            if (iOsChangeDirectionCounter.current < minimumForce) {
                iOsChangeDirectionCounter.current++;
            }
            else {
                iOsChangeDirectionCounter.current = 0;
                iOsFirstClientY.current = clientY;
                iOsScrollDirection.current = '';
            }
            return true;
        }
        iOsChangeDirectionCounter.current = 0;
        iOsLastClientY.current = clientY;
        return false;
    };
    return (React.createElement("div", { className: `scrollhandler-wrapper ${fixed ? 'fixed' : ''} ${props.loaded ? '' : 'landing'}`, ref: setRefs },
        React.createElement("div", { className: "scrollhandler", ref: scrollHandlerRef },
            React.createElement("div", { "aria-hidden": "true", tabIndex: -1, className: "scrollhandler-track", ref: trackRef, onMouseDown: jumpTo },
                React.createElement("button", { "aria-hidden": "true", tabIndex: -1, ref: thumbRef, type: "button", className: `scrollhandler-thumb ${active ? 'active' : ''}`, style: { transform: 'translate3d(-50%,' + currentY.current + 'px, 0)' }, onMouseDown: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        dragStart(e.clientY);
                    }, onTouchStart: (e) => {
                        var _a;
                        e.preventDefault();
                        e.stopPropagation();
                        const clientY = (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientY;
                        iOsScrollDirection.current = '';
                        iOsFirstClientY.current = clientY;
                        dragStart(clientY);
                    } })),
            React.createElement("div", { className: "scrollhandler-scrolldown" },
                React.createElement("button", { type: "button", "aria-hidden": "true", tabIndex: -1, onClick: scrollToBottom },
                    React.createElement("span", null))))));
};
export default ScrollHandler;
